import {Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from "@angular/core";
import {Store} from "@ngrx/store";
import {FormControl, FormGroup, NonNullableFormBuilder, ValidatorFn, Validators} from "@angular/forms";
import {CopyFundReportAndValuationsActions} from "../../../store/fund.actions";
import {BehaviorSubject, combineLatest, map, Subscription} from "rxjs";
import {Sort} from "@angular/material/sort";
import {CopyConfig, FundValuationCopyConfig} from "../../../models/copy-config";
import {selectCopyConfig, selectFundValuationsForFundReportToCopy} from "../../../store/fund/fund.selectors";
import {selectSelectedFundReportsForFund} from "../../../store/fund-report/fund-report.selectors";
import {DateUtil} from "../../../../shared/utils/date-util";
import {dateNotBeforeValidator} from "../../../../shared/utils/form-validators";
import {IMMEDIATE_ERROR_STATE_MATCHER} from "../../../../shared/utils/error-state-matcher";
import {FundValuation} from "../../../models/fund-valuation";
import {FundReport} from "../../../models/fund-report";
import {FundValuationTypeEnum} from "../../../models/fund-valuation-type.enum";
import {selectSettingValue} from "../../../../shared/user-setting/store/user-setting.selectors";
import {FUND_UNFUNDED_PARAMS_AUTO_CALC_KEY, FUND_UNFUNDED_PARAMS_KEY, isUnfundedParamsValid, UnfundedParams} from "../../../../settings/settings-keys";
import {sortByIsoDateBlanksLast} from "../../../../shared/utils/sort-util";

export type CopyFundValuationFormGroup = FormGroup<{
    fundValuationId: FormControl<number>;
    copyUndrawnValuation: FormControl<boolean>;
    copyBidPriceAdjustment: FormControl<boolean>;
}>

@Component({
    selector: "valumize-fund-valuation-copy-dialog",
    templateUrl: "./fund-valuation-copy-dialog.component.html",
    styleUrl: "./fund-valuation-copy-dialog.component.scss",
    standalone: false
})
export class FundValuationCopyDialogComponent implements OnInit, OnChanges, OnDestroy {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    @Input() fundId?: number;
    @Input() fundReportId?: number;

    @Output() previous = new EventEmitter<void>();
    @Output() completed = new EventEmitter<void>();

    subscriptions: Subscription[] = [];
    copyConfig?: CopyConfig;
    selectedFundValuationId?: number;
    private displayedValuations: FundValuation[] = [];
    private reports: FundReport[] = [];

    // The closing date must not fall before the destination report's date. The destination is the chosen
    // target report (valuation path) or the freshly-copied report from the copy config (report path).
    private readonly closingDateNotBeforeReportValidator: ValidatorFn = (control) => dateNotBeforeValidator(this.getComparisonReportDate())(control);

    // Show the closing-date error immediately (see IMMEDIATE_ERROR_STATE_MATCHER): the value is
    // pre-filled from the copy config, so the default matcher would hide the error until interaction.
    readonly closingDateErrorMatcher = IMMEDIATE_ERROR_STATE_MATCHER;

    fundValuations$ = this.store.select(selectFundValuationsForFundReportToCopy);
    fundReports$ = this.store.select(selectSelectedFundReportsForFund);

    // Show the undrawn auto-calc hint only when the behaviour is actually active: the org/user has
    // enabled auto-calc AND configured a valid unfunded-parameters setting. Mirrors the backend gate.
    showUndrawnAutoCalcHint$ = combineLatest([
        this.store.select(selectSettingValue(FUND_UNFUNDED_PARAMS_AUTO_CALC_KEY)),
        this.store.select(selectSettingValue(FUND_UNFUNDED_PARAMS_KEY))
    ]).pipe(map(([autoCalc, params]) => autoCalc === true && isUnfundedParamsValid(params as UnfundedParams)));
    fundValuationsColumns = ["selected", "closingDate", "description", "copyUndrawnValuation", "copyBidPriceAdjustment"];

    // Closing Date sort (WI-1239). Declared ABOVE displayedFundValuations$ because field initializers run
    // top-to-bottom and the stream references it. Sorting is done at the DATA level (not in the template)
    // so the positional valuationsFormArray is rebuilt in the same order and the checkboxes stay in sync.
    sortDirection: "asc" | "desc" = "asc";
    private readonly sortDirection$ = new BehaviorSubject<"asc" | "desc">("asc");

    onSortChange(sort: Sort) {
        this.sortDirection = sort.direction === "desc" ? "desc" : "asc";
        this.sortDirection$.next(this.sortDirection);
    }

    // In the valuation path only the source valuation is relevant, so collapse the list to that single
    // row. In the report path (fundReportCopyConfig set) all of the report's valuations are shown.
    displayedFundValuations$ = combineLatest([this.fundValuations$, this.store.select(selectCopyConfig), this.sortDirection$]).pipe(
        map(([valuations, copyConfig, dir]) => {
            const isValuationPath = !copyConfig.fundReportCopyConfig;
            const sourceFundValuationId = copyConfig.fundValuationCopyConfig?.fundValuationId;
            const filtered = isValuationPath && sourceFundValuationId
                ? valuations.filter(valuation => valuation.id === sourceFundValuationId)
                : valuations;
            return sortByIsoDateBlanksLast(filtered, v => v.closingDate?.date, dir);
        })
    );

    /**
     * Target Fund Report dropdown: always ascending by report date, empties last (WI-1239).
     *
     * Derived once per emission rather than called from the template — `@for (report of sortReportsByDate(...))`
     * rebuilt the array on every change-detection pass.
     */
    sortedFundReports$ = this.fundReports$.pipe(
        map(reports => sortByIsoDateBlanksLast(reports, r => r.reportDate?.date))
    );

    fundValuationCopyForm = this.formBuilder.group({
        selectedFundValuationId: this.formBuilder.control<number | null>(null, {validators: [Validators.required]}),
        valuations: this.formBuilder.array<CopyFundValuationFormGroup>([]),
        closingDate: this.formBuilder.control<Date | null>(null),
        description: this.formBuilder.control<string>("", {validators: [Validators.required, Validators.maxLength(30)]}),
        targetFundReportId: this.formBuilder.control<number | null>(null)
    });

    get valuationsFormArray() {
        return this.fundValuationCopyForm.controls.valuations;
    }

    constructor( ) {
    }

    ngOnInit() {
        const closingDateControl = this.fundValuationCopyForm.controls.closingDate;
        closingDateControl.setValidators([Validators.required, this.closingDateNotBeforeReportValidator]);

        // The comparison report date changes with the selected target report and the available reports;
        // re-run the closing-date validation whenever either changes.
        this.subscriptions.push(
            this.fundReports$.subscribe(reports => {
                this.reports = reports;
                closingDateControl.updateValueAndValidity({emitEvent: false});
            }));
        this.subscriptions.push(
            this.fundValuationCopyForm.controls.targetFundReportId.valueChanges.subscribe(() =>
                closingDateControl.updateValueAndValidity({emitEvent: false})));

        this.subscriptions.push(
            this.store.select(selectCopyConfig).pipe(map(copyConfig => {
                this.copyConfig = copyConfig;
                this.selectedFundValuationId = copyConfig.fundValuationCopyConfig?.fundValuationId;

                this.getSelectedFundValuationIdControl().setValue(this.selectedFundValuationId ?? null);
                this.fundValuationCopyForm.patchValue({
                    closingDate: DateUtil.fromIsoDate(copyConfig.fundValuationCopyConfig?.closingDate)?.toJSDate() ?? DateUtil.jsDate(),
                    description: copyConfig.fundValuationCopyConfig?.description
                });

                // The target fund report is mandatory in the valuation path (the copy must land under a
                // report); in the report path the copy goes under the freshly-copied report, so it's unused.
                const targetControl = this.fundValuationCopyForm.controls.targetFundReportId;
                // Set the target silently ({emitEvent: false}): the single closing-date revalidation below
                // covers this config-driven change, so we avoid a redundant pass via the valueChanges
                // subscription (which stays for genuine user selections in the dropdown).
                if (this.isValuationPath()) {
                    targetControl.setValidators([Validators.required]);
                    if (targetControl.value == null) {
                        targetControl.setValue(copyConfig.fundValuationCopyConfig?.targetFundReportId ?? this.fundReportId ?? null, {emitEvent: false});
                    }
                } else {
                    targetControl.clearValidators();
                    targetControl.setValue(null, {emitEvent: false});
                }
                targetControl.updateValueAndValidity({emitEvent: false});
                // Report-path comparison date comes from the copy config, and the target was set silently
                // above, so revalidate the closing date once here for both paths.
                this.fundValuationCopyForm.controls.closingDate.updateValueAndValidity({emitEvent: false});
            })).subscribe());

        this.subscriptions.push(
            this.displayedFundValuations$.pipe(map(valuations => {
                this.displayedValuations = valuations;
                // Re-sorting re-emits this stream and rebuilds the positional form array. Capture the
                // current per-valuation checkbox values first and re-apply them by fundValuationId so a
                // sort toggle doesn't reset the user's Copy Undrawn / Copy Bid Price choices (WI-1239, C3).
                const prior = new Map(this.valuationsFormArray.controls.map(group => [
                    group.controls.fundValuationId.value,
                    {undrawn: group.controls.copyUndrawnValuation.value, bid: group.controls.copyBidPriceAdjustment.value}
                ]));
                this.valuationsFormArray.clear();
                valuations.forEach(valuation => {
                    if (!valuation.id) return;
                    const fvCopyConfig = this.copyConfig?.fundValuationCopyConfig;
                    const preserved = prior.get(valuation.id);
                    const group: CopyFundValuationFormGroup = this.formBuilder.group({
                        fundValuationId: this.formBuilder.control(valuation.id),
                        copyUndrawnValuation: this.formBuilder.control(
                            preserved?.undrawn
                            ?? (fvCopyConfig?.fundValuationId === valuation.id ? fvCopyConfig.copyUndrawnValuation : false)),
                        copyBidPriceAdjustment: this.formBuilder.control(
                            preserved?.bid
                            ?? (fvCopyConfig?.fundValuationId === valuation.id ? fvCopyConfig.copyBidPriceAdjustment : false))
                    });
                    this.valuationsFormArray.push(group);
                })
                this.updateFormControls();
            })).subscribe());
    }

    ngOnChanges(changes: SimpleChanges) {
        if ((changes["fundId"] || changes["fundReportId"]) && this.fundId != null && this.fundReportId != null) {
            this.store.dispatch(CopyFundReportAndValuationsActions.loadFundValuations({fundId: this.fundId, fundReportId: this.fundReportId}));
        }
    }

    onSelectFundValuation(id: number, checked: boolean): void {
        this.selectedFundValuationId = checked ? id : undefined;
        this.getSelectedFundValuationIdControl().setValue(this.selectedFundValuationId ?? null);
        this.updateFormControls();
    }

    updateFormControls(): void {
        this.valuationsFormArray.controls.forEach((group) => {
            const isSelected = group.controls.fundValuationId.value === this.selectedFundValuationId;
            if (isSelected) {
                group.controls.copyUndrawnValuation.enable();
                group.controls.copyBidPriceAdjustment.enable();
            } else {
                group.controls.copyUndrawnValuation.setValue(false);
                group.controls.copyUndrawnValuation.disable();

                group.controls.copyBidPriceAdjustment.setValue(false);
                group.controls.copyBidPriceAdjustment.disable();
            }
        });
        const selected = this.displayedValuations.find(valuation => valuation.id === this.selectedFundValuationId);
        const isBottomUp = selected?.valuationType?.code === FundValuationTypeEnum.BottomUpValuation;
        this.store.dispatch(CopyFundReportAndValuationsActions.updateCopyWorkflow({copyAssetValuationsWorkflowStep: !!this.selectedFundValuationId && isBottomUp}));
    }

    isValuationPath = (): boolean => !this.copyConfig?.fundReportCopyConfig;

    // Destination report date to validate the closing date against: the selected target report in the
    // valuation path, or the copied report's date from the config in the report path. Undefined when not
    // yet resolved, in which case the validator passes (Validators.required on the target still gates Next).
    private getComparisonReportDate(): Date | undefined {
        if (this.isValuationPath()) {
            const targetId = this.fundValuationCopyForm.controls.targetFundReportId.value;
            return DateUtil.toJsDate(this.reports.find(report => report.id === targetId)?.reportDate?.date);
        }
        return DateUtil.toJsDate(this.copyConfig?.fundReportCopyConfig?.reportDate);
    }

    getSelectedFundValuationIdControl = (): FormControl<number | null> => this.fundValuationCopyForm.controls.selectedFundValuationId;

    getCopyUndrawnValuationControl = (index: number): FormControl<boolean> => this.valuationsFormArray.at(index).controls.copyUndrawnValuation;

    getCopyBidPriceAdjustmentControl = (index: number): FormControl<boolean> => this.valuationsFormArray.at(index).controls.copyBidPriceAdjustment;

    isRowSelected = (id: number): boolean => this.getSelectedFundValuationIdControl().value === id;

    navigateToFundReportCopyDialog() {
        this.updateCopyConfig();
        this.previous.emit();
    }

    navigateToAssetValuationCopyDialog() {
        this.updateCopyConfig();
        this.completed.emit();
    }

    navigateToSummaryPage() {
        this.updateCopyConfig();
        this.completed.emit();
    }

    private updateCopyConfig() {
        const fundValuationId = this.getSelectedFundValuationIdControl().value;
        const closingDate = DateUtil.toIsoDate(this.fundValuationCopyForm.controls.closingDate.value);
        const description = this.fundValuationCopyForm.controls.description.value;
        const selectedValuationFormGroup = this.valuationsFormArray.controls.find(group => group.controls.fundValuationId.value === fundValuationId);
        if (!fundValuationId || !closingDate || !description || !selectedValuationFormGroup || !this.fundValuationCopyForm.valid) {
            return;
        }

        const updatedFundValuationCopyConfig: FundValuationCopyConfig = {
            fundValuationId,
            closingDate,
            description,
            copyUndrawnValuation: selectedValuationFormGroup.controls.copyUndrawnValuation.value,
            copyBidPriceAdjustment: selectedValuationFormGroup.controls.copyBidPriceAdjustment.value,
            assetValuationsCopyConfig: this.copyConfig?.fundValuationCopyConfig?.assetValuationsCopyConfig ?? [],
            // Only the valuation path picks a destination report; the report path leaves it undefined.
            targetFundReportId: this.isValuationPath() ? (this.fundValuationCopyForm.controls.targetFundReportId.value ?? undefined) : undefined
        }
        this.copyConfig = {...this.copyConfig, fundValuationCopyConfig: updatedFundValuationCopyConfig};

        this.store.dispatch(CopyFundReportAndValuationsActions.updateCopyConfig({copyConfig: this.copyConfig}));
    }

    ngOnDestroy = () => {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.sortDirection$.complete();
    };
}
